// oxlint-disable typescript/no-unsafe-type-assertion

import { assertEvent, setup } from 'xstate';

import { type Context, type Event, type Input } from './scrollspy-virtualized.types';
import { Actions, Actors, createInitialContext, Guards } from './setup';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the active scrollspy target for a virtualized list.
 *
 * A jump records a pending target, and viewability is ignored until the list
 * reports it. Virtualized lists report the rows they pass on the way, including
 * the ones rendered before an initial scroll lands, so an ungated report would
 * activate whatever the list happened to be showing. The list is held on the
 * target while its layout settles, since rows measured after a jump lands can
 * push the target out of view.
 *
 * Viewability reports list the visible targets with the leading one first.
 */
export const scrollspyVirtualizedMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as Input,
  },
  actions: {
    activateRequestedTarget: Actions.activateRequestedTarget,
    activateVisibleTarget: Actions.activateVisibleTarget,
    clearPendingTarget: Actions.clearPendingTarget,
    recomputeVisibleTargets: Actions.recomputeVisibleTargets,
    onActiveTargetChange: Actions.onActiveTargetChange,
  },
  actors: {
    anchorRequestedTarget: Actors.anchorRequestedTarget,
  },
  guards: {
    checkIfNoPendingTarget: Guards.checkIfNoPendingTarget,
    checkIfPendingTargetReached: Guards.checkIfPendingTargetReached,
    checkIfVisibleTargetChanged: Guards.checkIfVisibleTargetChanged,
  },
  delays: {
    PENDING_TARGET_TIMEOUT: 500,
    ANCHOR_TIMEOUT: 5000,
  },
}).createMachine({
  id: 'scrollspy-virtualized',
  context: createInitialContext,
  initial: 'awaiting-target',
  states: {
    'awaiting-target': {
      description: 'Holding the requested target until the list reports it as visible.',
      always: { guard: 'checkIfNoPendingTarget', target: 'tracking' },
      on: {
        SET_VISIBLE_TARGETS: {
          guard: 'checkIfPendingTargetReached',
          target: 'tracking',
          actions: ['clearPendingTarget'],
        },
      },
      after: {
        PENDING_TARGET_TIMEOUT: {
          target: 'tracking',
          actions: ['clearPendingTarget', 'recomputeVisibleTargets'],
        },
      },
    },
    'anchoring-target': {
      description: 'Keeping the list on the requested target while its layout settles.',
      invoke: {
        src: 'anchorRequestedTarget',
        input: ({ context, event }) => {
          assertEvent(event, 'SCROLL_TO_TARGET');

          const { listRef, findTargetIndex } = context;

          return { listRef, findTargetIndex, targetId: event.targetId };
        },
        onDone: { target: 'awaiting-target', actions: ['recomputeVisibleTargets'] },
        onError: { target: 'awaiting-target', actions: ['recomputeVisibleTargets'] },
      },
      after: {
        ANCHOR_TIMEOUT: { target: 'awaiting-target', actions: ['recomputeVisibleTargets'] },
      },
    },
    tracking: {
      description: 'The first visible target drives the active one.',
      on: {
        SET_VISIBLE_TARGETS: {
          guard: 'checkIfVisibleTargetChanged',
          actions: ['activateVisibleTarget', 'onActiveTargetChange'],
        },
      },
    },
  },
  on: {
    SCROLL_TO_TARGET: {
      target: '.anchoring-target',
      actions: ['activateRequestedTarget', 'onActiveTargetChange'],
    },
  },
});
